<template>
  <div
    class="w-full bg-white py-[26px] overflow-y-auto overscroll-none"
    :style="styles"
  >
    <div class="px-5 flex flex-col items-center mb-[22px]">
      <Avatar :size="60" :src="user?.avatarUrl" class="mb-3" />

      <div class="mb-[22px]">
        <div
          v-if="user"
          class="font-semibold font-primary text-[24px] text-gray-800"
        >
          {{ user.name }}
        </div>
        <Skeleton v-else class="w-[200px] h-9" />
      </div>

      <MobilePlanCallout @close="emit('close')" />

      <UpsellBanner
        v-if="billingStore.billing?.upsell"
        class="px-4 py-3 flex-col! mt-2.5 max-w-[388px]"
      />
    </div>

    <div v-if="!isBasic" class="px-5 font-secondary text-[14px] text-gray-800">
      <div v-if="user" class="flex items-center mb-1 last:mb-0">
        <div class="mr-1 font-medium">
          {{ totalFilesCopy }}
        </div>
        <div class="opacity-60">
          {{
            pluralize('copyright', user.totalFilesLimit || user.totalFiles || 0)
          }}
        </div>
      </div>

      <template v-if="user?.upcomingBilling">
        <div class="flex items-center mb-1 last:mb-0">
          <div class="mr-1 font-medium">
            {{ formatUpcomingBilling(user.upcomingBilling) }}
          </div>
          <div class="opacity-60">
            due on {{ prettyDateFormat(user.upcomingBilling.date) }}
          </div>
        </div>
      </template>

      <div v-if="user?.pendingSales" class="flex items-center mb-1 last:mb-0">
        <div class="mr-1 font-medium">
          {{ user.pendingSales }}
        </div>
        <div class="opacity-60">
          pending {{ pluralize('sale', user.pendingSales) }}
        </div>
      </div>
      <div
        v-if="user?.pendingTransfers"
        class="flex items-center mb-1 last:mb-0"
      >
        <div class="mr-1 font-medium">
          {{ user.pendingTransfers }}
        </div>
        <div class="opacity-60">
          pending {{ pluralize('transfer', user.pendingTransfers) }}
        </div>
      </div>
      <div
        v-if="user?.pendingLicenses"
        class="flex items-center mb-1 last:mb-0"
      >
        <div class="mr-1 font-medium">
          {{ user.pendingLicenses }}
        </div>
        <div class="opacity-60">
          pending {{ pluralize('license', user.pendingLicenses) }}
        </div>
      </div>
      <div
        v-if="isPro"
        class="font-secondary text-[12px] leading-[16px] text-gray-500 slant mt-[14px]"
      >
        *Copyrights are billed one hour after they are uploaded
      </div>
    </div>

    <LargeSeparator />

    <TallListItem icon="Settings" @click="handleAccountClick">
      Account settings
    </TallListItem>

    <TallListItem icon="App">
      <a href="/download" target="_blank">Download Dacr app</a>
    </TallListItem>

    <a v-if="isAffiliate" :href="affiliateUrl" target="_blank">
      <TallListItem icon="External"> Affiliate dashboard </TallListItem>
    </a>

    <LargeSeparator />

    <TallListItem icon="Info">
      <a href="https://help.dacr.com">Help center</a>
    </TallListItem>

    <LargeSeparator />

    <TallListItem icon="Signout" @click="handleSignOutClick">
      Sign out
    </TallListItem>
  </div>
</template>

<script setup lang="ts">
import { useDevice } from '@/shared/composables/useDevice'
import { MOBILE_AUTH_NAVBAR_HEIGHT } from '@/shared/composables/useAvailableHeight'
import { computed } from 'vue'
import { Avatar, Skeleton } from '@dacr/ui'
import { useUserStore } from '@/shared/stores/user'
import {
  PlanType,
  UserPermission,
  type UserFieldsFragment,
} from '@/shared/graphql/legacy/generated'
import { useRouter } from 'vue-router'
import { formatTimestamp } from '@/shared/utils/datetime'
import { formatPrice } from '@/shared/utils/money'
import { LargeSeparator, TallListItem } from './Menu'
import { useUploadStore } from '@/features/upload/store'
import MobilePlanCallout from '@/features/billing/components/MobilePlanCallout.vue'
import pluralize from 'pluralize'
import { formatNumber } from '@/shared/utils/number'
import UpsellBanner from '@/features/billing/components/UpsellBanner.vue'
import { useBillingStore } from '@/features/billing/store'

const emit = defineEmits(['showSettings', 'showNotifications', 'close'])

const router = useRouter()
const device = useDevice()
const billingStore = useBillingStore()
const userStore = useUserStore()
const uploadStore = useUploadStore()

const user = computed(() => userStore.user)

const isBasic = computed(
  () => !user.value?.permissions.includes(UserPermission.Pro)
)

const isPro = computed(() => billingStore.billing?.plan.type === PlanType.Pro)

const styles = computed(() => ({
  ...((device.isMobile.value || device.isTabletVertical.value) && {
    height: `${device.viewportHeight.value - MOBILE_AUTH_NAVBAR_HEIGHT}px`,
  }),
}))

const totalFilesCopy = computed(() =>
  typeof user.value?.totalFilesLimit === 'number'
    ? `${formatNumber(user.value?.totalFiles || 0)} of ${formatNumber(user.value?.totalFilesLimit || 0)}`
    : formatNumber(user.value?.totalFiles || 0)
)

const isAffiliate = computed(() =>
  user.value?.permissions.includes(UserPermission.Affiliate)
)

const affiliateUrl = computed(() => import.meta.env.VITE_AFFILIATE_URL)

const prettyDateFormat = (date: number) => {
  return formatTimestamp(date, {
    formatString: user.value?.dateFormat,
  })
}

const handleAccountClick = () => {
  emit('showSettings')
}

const formatUpcomingBilling = (
  upcomingBilling: UserFieldsFragment['upcomingBilling']
) => {
  if (!upcomingBilling) return

  return formatPrice({
    amount: upcomingBilling.total / 100,
    currency: upcomingBilling.currency,
  })
}

const handleSignOutClick = () => {
  uploadStore.showUploadBar = false
  router.push('/signout')
}
</script>
